/**
 * Device-local privacy choices. The server record is the one other modules
 * trust. Local storage only remembers the choice so the banner can stay
 * closed and so third-party scripts stay blocked on the next page load.
 *
 * The visible sentences are placeholder copy until the owner approves the
 * privacy-policy wording. Do not treat them as final policy text.
 */
export const PRIVACY_CHOICE_STORAGE_KEY = "plateOS_privacy_choices";
export const PRIVACY_SUBJECT_STORAGE_KEY = "plateOS_privacy_subject";
export const PRIVACY_OPTIONAL_BLOCK_STORAGE_KEY = "plateOS_optional_tracking_blocked";
export const PRIVACY_POLICY_VERSION = "privacy-choices-2026-10-15";
export function globalPrivacyControlEnabled(): boolean {
  return typeof navigator !== "undefined" && (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}
const CHOICE_EVENT = "plateOS:privacy-choice-changed";
export const OPEN_PRIVACY_CHOICES_EVENT = "plateOS:open-privacy-choices";

export function openPrivacyChoices(): void {
  window.dispatchEvent(new CustomEvent(OPEN_PRIVACY_CHOICES_EVENT));
}

export function subscribePrivacyChoices(listener: () => void): () => void {
  const storage = (event: StorageEvent) => {
    if (event.key === null || event.key === PRIVACY_CHOICE_STORAGE_KEY || event.key === PRIVACY_OPTIONAL_BLOCK_STORAGE_KEY) {
      // Another tab's confirmed choice can replace this tab's pending block.
      if (event.key === PRIVACY_OPTIONAL_BLOCK_STORAGE_KEY && event.newValue === null) optionalCollectionBlockedInMemory = false;
      listener();
    }
  };
  window.addEventListener(CHOICE_EVENT, listener);
  window.addEventListener("storage", storage);
  return () => {
    window.removeEventListener(CHOICE_EVENT, listener);
    window.removeEventListener("storage", storage);
  };
}

function notifyPrivacyChoices(): void {
  window.dispatchEvent(new CustomEvent(CHOICE_EVENT));
}

const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
let optionalCollectionBlockedInMemory = false;

function availablePrivacyStorage(): Storage[] {
  const stores: Storage[] = [];
  try {
    if (typeof localStorage !== "undefined") stores.push(localStorage);
  } catch {
    // Storage access can be denied by browser privacy settings.
  }
  try {
    if (typeof sessionStorage !== "undefined") stores.push(sessionStorage);
  } catch {
    // Keep the other available storage, if any.
  }
  return stores;
}

export interface PrivacyChoices {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  policyVersion: string;
  updatedAt: string;
  action: "grant" | "update" | "withdraw";
}

export function readPrivacyChoices(): PrivacyChoices | null {
  try {
    const raw = localStorage.getItem(PRIVACY_CHOICE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PrivacyChoices>;
    if (parsed.policyVersion !== PRIVACY_POLICY_VERSION) return null;
    if (typeof parsed.analytics !== "boolean" || typeof parsed.marketing !== "boolean") return null;
    if (parsed.action !== "grant" && parsed.action !== "update" && parsed.action !== "withdraw") return null;
    return {
      necessary: true,
      analytics: parsed.action === "withdraw" ? false : parsed.analytics,
      marketing: parsed.action === "withdraw" ? false : parsed.marketing,
      policyVersion: PRIVACY_POLICY_VERSION,
      updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : "",
      action: parsed.action,
    };
  } catch {
    return null;
  }
}

export function writePrivacyChoices(choice: {
  analytics: boolean;
  marketing: boolean;
  action: PrivacyChoices["action"];
}): PrivacyChoices {
  const stored: PrivacyChoices = {
    necessary: true,
    analytics: choice.action === "withdraw" ? false : choice.analytics,
    marketing: choice.action === "withdraw" ? false : choice.marketing,
    policyVersion: PRIVACY_POLICY_VERSION,
    updatedAt: new Date().toISOString(),
    action: choice.action,
  };
  let savedLocally = false;
  try {
    localStorage.setItem(PRIVACY_CHOICE_STORAGE_KEY, JSON.stringify(stored));
    savedLocally = true;
  } catch {
    // The server record still counts, but do not remove a local safety block
    // if the previous local grant could not be replaced.
  }
  if (savedLocally) {
    clearOptionalCollectionBlock();
  }
  notifyPrivacyChoices();
  return stored;
}

/**
 * A pending opt-out is not consent or a saved choice. It only fails closed
 * locally until the server confirms the member's explicit update.
 */
export function blockOptionalCollection(): void {
  optionalCollectionBlockedInMemory = true;
  for (const storage of availablePrivacyStorage()) {
    try {
      storage.setItem(PRIVACY_OPTIONAL_BLOCK_STORAGE_KEY, "1");
    } catch {
      // Keep the in-memory block for this page if browser storage is disabled.
    }
  }
  notifyPrivacyChoices();
}

export function clearOptionalCollectionBlock(): void {
  optionalCollectionBlockedInMemory = false;
  for (const storage of availablePrivacyStorage()) {
    try {
      storage.removeItem(PRIVACY_OPTIONAL_BLOCK_STORAGE_KEY);
    } catch {
      // A failed removal is still safe: the persisted block keeps tracking off.
    }
  }
}

export function isOptionalCollectionBlocked(): boolean {
  if (optionalCollectionBlockedInMemory) return true;
  for (const storage of availablePrivacyStorage()) {
    try {
      if (storage.getItem(PRIVACY_OPTIONAL_BLOCK_STORAGE_KEY) === "1") return true;
    } catch {}
  }
  return false;
}

export function analyticsAllowed(): boolean {
  if (globalPrivacyControlEnabled()) return false;
  if (isOptionalCollectionBlocked()) return false;
  return readPrivacyChoices()?.analytics === true;
}

export function marketingAllowed(): boolean {
  if (globalPrivacyControlEnabled()) return false;
  if (isOptionalCollectionBlocked()) return false;
  return readPrivacyChoices()?.marketing === true;
}

export function privacySubjectId(): string {
  try {
    const existing = localStorage.getItem(PRIVACY_SUBJECT_STORAGE_KEY);
    if (existing && UUID_V4.test(existing)) return existing;
    const created = crypto.randomUUID();
    localStorage.setItem(PRIVACY_SUBJECT_STORAGE_KEY, created);
    return created;
  } catch {
    return crypto.randomUUID();
  }
}
